import { EProductTypes } from '../enums';
import {
    ExpiryTypes,
    IPoint,
    IPrice,
    ITenor,
    ITradePadBrandingConfig,
    MarketType,
    PendingPriceType,
    PendingType,
    QuoteResponse,
    QuoteSideTypeEnum,
    SecuritySymbol,
    SpotType,
    SymbolTypes,
} from '@fnx/fx-pad';
import { ICcypair, ISO4217 } from '@alk/currency';
import { ISimpleNotification } from './simple-notification.interface';
import { ISO8601, Tenor } from '@alk/dates';
import { Observable } from 'rxjs';
import { RequiredKeys } from '../types';

export interface IPriceService {
    quoteObservable(request: Partial<IQuoteRequest>): Observable<IQuoteResponse>;
}

export interface IQuoteRequest {
    // Tenor interface doesn't allow to use 1W or 2D so temporarily fallback to string
    date: ISO8601 | Tenor;
    dateFar: ISO8601 | Tenor;
    tradedCurrency: ISO4217;
    ccyPair: ICcypair;
    quantity: number;
    quantitySecondLeg?: number;
    productType: SymbolTypes;
    spotType: SpotType;
    farQuantity: number;
    side?: QuoteSideTypeEnum;
    price?: number;
}

export type IQuoteResponse = IQuote | Array<IQuote>;

export interface IQuote {
    requestId: number;
    productType: EProductTypes;
    quoteId?: string;

    askPrice?: IPrice;
    spotAskPrice?: IPrice;
    askPointsNear?: IPoint;
    askPriceFar?: IPrice;
    askPointsFar?: IPoint;
    askSwapRate?: IPoint;

    bidPrice?: IPrice;
    spotBidPrice?: IPrice;
    bidPointsNear?: IPoint;
    bidPriceFar?: IPrice;
    bidPointsFar?: IPoint;
    bidSwapRate?: IPoint;

    spotSpread?: IPoint;
    forwardSpread?: IPoint;
    swapSpread?: IPoint;

    swapMidRate?: IPrice;

    askTimestamp?: number,
    bidTimestamp?: number,
}

export type SecuritySymbolExtended = SecuritySymbol & { isDefault?: boolean };
export type SecuritySymbolWithType = SecuritySymbolExtended & { type: SymbolTypes };

export type IPadConfig = Pick<IPadResult,
'id'
| 'instrument'
| 'tradedCurrency'
| 'quantity'
| 'quantitySecondLeg'
| 'date'
| 'dateFar'
| 'productType'
| 'spotType'
| 'pendingType'
| 'farQuantity'
| 'expiryDate'
| 'expiryType'
| 'triggerPrice'
| 'triggerPriceSecondLeg'
| 'selectedTradeSide'
| 'selectedTradeSideSecondLeg'
| 'notification'
| 'priceType'
| 'priceTypeSecondLeg'
>;

export const PAD_CONFIG_REQUIRED_PROPS: ReadonlyArray<RequiredKeys<IPadConfig>> = [
    'id', 'instrument', 'tradedCurrency', 'productType', 'spotType',
] as const;

export type IStartedPadConfig = IPadConfig & Pick<IPadResult, 'isStartRequested'>;

export type IPadResult = {
    id: string;
    instrument: SecuritySymbol;
    tradedCurrency: ISO4217;
    quantity?: number;
    quantitySecondLeg?: number;
    date?: ISO8601 | Tenor;
    dateFar?: ISO8601 | Tenor;
    productType: SymbolTypes;
    spotType: MarketType;
    pendingType: PendingType;
    availableOrderTypes: Array<SpotType>;
    farQuantity?: number;
    isStartRequested: boolean;
    isQuoteFeedAlive: boolean;
    instruments: Array<SecuritySymbolWithType>;
    allInstruments: Array<SecuritySymbolWithType>;
    quote?: QuoteResponse;
    quoteList?: Array<QuoteResponse>;
    resolvedTenors: Array<ITenor>;

    config: ITradePadBrandingConfig;

    hasConfirmation: boolean;
    displaySpread: boolean;

    tradingDisabled: boolean;
    isSpeedBumpActive?: boolean;

    /**
     * Properties specific for Pending
     */
    expiryType?: ExpiryTypes;
    expiryDate?: ISO8601;
    triggerPrice?: number;
    triggerPriceSecondLeg?: number;
    selectedTradeSide?: QuoteSideTypeEnum;
    selectedTradeSideSecondLeg?: QuoteSideTypeEnum;
    priceType?: PendingPriceType;
    priceTypeSecondLeg?: PendingPriceType;

    notification?: ISimpleNotification
    isCurrencyInversionForESPEnable: boolean;
    isCurrencyInversionForRFSEnable: boolean;
};

export type IDefaultPadsLayoutParams = {
    tradableCurrencies: Array<SecuritySymbol>,
    defaultPadType: SymbolTypes,
};

export type PadConfigExtended =
    IPadConfig
    & Pick<IPadResult,
    'isStartRequested'
    | 'instruments'
    | 'allInstruments'
    | 'quote'>;
